"use client";

import { useEffect } from "react";

/**
 * Android (Capacitor) Google OAuth completes in the system browser (Chrome
 * Custom Tabs via @capacitor/browser) and returns through the vortixy://auth/callback
 * deep link — the same link the Electron desktop app uses. This handler forwards
 * that URL into the WebView so the Next.js /auth/callback route exchanges the code and
 * the session cookies land on https://www.vortixy.net, mirroring Electron's
 * handleDeepLink() in electron/main.js.
 *
 * 2026-08-30 fix — verified 2026 Capacitor 7 docs + Supabase PKCE guide:
 * - AndroidManifest host was "auth/callback" (invalid — host cannot contain slash) so
 *   intent-filter never matched vortixy://auth/callback?code=... → app never received URL.
 *   Fixed to host="auth" pathPrefix="/callback" (see android/app/src/main/AndroidManifest.xml).
 * - Must handle BOTH cold-start (getLaunchUrl) and warm (appUrlOpen) — previously only warm.
 * - Must close Chrome Custom Tabs (Browser.close) after handoff, otherwise user sees blank tab.
 * - Robust URL parsing: Supabase PKCE may return code in query or hash fragment.
 *
 * Mounted from the root layout; it is a no-op on web and desktop (the
 * @capacitor/app import is lazy so the plain web bundle never loads it).
 */
export function NativeAuthDeepLinkHandler() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const capacitor = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
    if (!capacitor?.isNativePlatform?.()) return;

    let cancelled = false;
    let removeListener: (() => void) | null = null;

    const handleUrl = async (url: string) => {
      if (!url || typeof url !== "string") return;
      // Accept any vortixy://auth/callback variant (query or hash code)
      if (!url.startsWith("vortixy://")) return;
      // Only handle auth callbacks here; other deep links could be routed elsewhere
      if (!url.includes("auth/callback") && !url.includes("auth%2Fcallback")) return;

      // Close the Chrome Custom Tab that was opened for Google OAuth
      try {
        const { Browser } = await import("@capacitor/browser");
        await Browser.close().catch(() => {});
      } catch {
        // Browser plugin unavailable in this context — ignore
      }

      // Reconstruct https URL preserving search + hash (Supabase PKCE code may be in either)
      let webUrl: string;
      try {
        const parsed = new URL(url);
        const fullPath = `${parsed.host}${parsed.pathname}`;
        const normalized = fullPath.startsWith("/") ? fullPath : `/${fullPath}`;
        webUrl = `https://www.vortixy.net${normalized}${parsed.search}${parsed.hash}`;
        // Fallback for URLs where parsing missed query (rare)
        if (!parsed.search && url.includes("?")) {
          const q = url.slice(url.indexOf("?"));
          if (!webUrl.includes("?")) webUrl += q;
        }
      } catch {
        webUrl = `https://www.vortixy.net/${url.slice("vortixy://".length)}`;
      }

      window.location.href = webUrl;
    };

    void import("@capacitor/app").then(async ({ App }) => {
      if (cancelled) return;

      // Cold start: app was not running when vortixy:// was opened (Android singleTask)
      try {
        const launch = await App.getLaunchUrl();
        if (launch?.url) {
          void handleUrl(launch.url);
        }
      } catch {
        // getLaunchUrl may not be available on older Capacitor — ignore
      }

      const handle = await App.addListener("appUrlOpen", ({ url }) => {
        void handleUrl(url);
      });
      removeListener = handle.remove;
    });

    return () => {
      cancelled = true;
      removeListener?.();
    };
  }, []);

  return null;
}
